import { QueryClient } from '@tanstack/react-query';
import type { SchoolWishlistMembership } from '../../api/school-wishlist/get-school-wishlist-membership';
import { schoolWishlistKeys } from './query-keys/query-keys';

export type SchoolWishlistSnapshot = {
    previousMembership: SchoolWishlistMembership | undefined;
    previousTotal: number | undefined;
};

// Writes a toggle into the material's membership and the school total before the request settles. The total moves
// only when this tab knew the material's previous state; otherwise the settle refetch brings the exact number.
export const applyOptimisticSchoolWishlistChange = async (
    queryClient: QueryClient,
    materialId: number,
    nextMembership: SchoolWishlistMembership
): Promise<SchoolWishlistSnapshot> => {
    const membershipKey = schoolWishlistKeys.membership(materialId);
    const totalKey = schoolWishlistKeys.total();

    await Promise.all([
        queryClient.cancelQueries({ queryKey: membershipKey }),
        queryClient.cancelQueries({ queryKey: totalKey })
    ]);

    const previousMembership = queryClient.getQueryData<SchoolWishlistMembership>(membershipKey);
    const previousTotal = queryClient.getQueryData<number>(totalKey);
    const snapshot = { previousMembership, previousTotal };

    if (previousMembership?.isInWishlist === nextMembership.isInWishlist) {
        return snapshot;
    }

    queryClient.setQueryData<SchoolWishlistMembership>(membershipKey, nextMembership);

    if (previousMembership && previousTotal !== undefined) {
        queryClient.setQueryData<number>(totalKey, Math.max(0, previousTotal + (nextMembership.isInWishlist ? 1 : -1)));
    }

    return snapshot;
};

export const rollbackSchoolWishlistChange = (
    queryClient: QueryClient,
    materialId: number,
    { previousMembership, previousTotal }: SchoolWishlistSnapshot
) => {
    queryClient.setQueryData(schoolWishlistKeys.membership(materialId), previousMembership);
    queryClient.setQueryData(schoolWishlistKeys.total(), previousTotal);
};

// Refetches only what one toggle can change: the material's membership and the school total.
export const invalidateSchoolWishlistMaterial = (queryClient: QueryClient, materialId: number) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: schoolWishlistKeys.membership(materialId) }),
        queryClient.invalidateQueries({ queryKey: schoolWishlistKeys.total() })
    ]);
